import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';

type UseOpenCheckoutSessionActionProps = {
    companyId: string;
};

/** The Stripe price the company is subscribing to, and in which currency. */
type OpenCheckoutSessionVariables = {
    priceId: string;
    currency: string;
};

type UseOpenCheckoutSessionActionData = {
    openCheckoutSession: (priceId: string, currency: string) => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Starting a subscription: hands the browser to Stripe's hosted checkout for
 * the chosen price, and tells it where to come back to either way.
 *
 * Carries no `isAllowed` of its own: the billing tab is gated as a whole by
 * `canEditBilling` on the billing settings page, and everything on it is
 * reachable only from there.
 */
export function useOpenCheckoutSessionAction(
    props: UseOpenCheckoutSessionActionProps
): UseOpenCheckoutSessionActionData {
    const { companyId } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const mutation = useMutation({
        mutationFn: ({ priceId, currency }: OpenCheckoutSessionVariables) => {
            const hostURL = window.location.origin;

            const successUrl = `${hostURL}/company/${companyId}/settings/billing?subscription=success`;
            const cancelUrl = `${hostURL}/company/${companyId}/settings/billing`;

            return openApiRef.current.companies
                .createStripeCheckoutSession(companyId, {
                    priceId,
                    currency,
                    successUrl,
                    cancelUrl
                })
                .then((response) => {
                    // Stripe hosts the checkout, so the browser leaves the app
                    // here. Nothing to write to the cache on the way out.
                    window.location.replace(response.data.data.url);
                });
        },
        onError: () => {
            // Deliberately one message for every error this endpoint declares.
            // Whether the refusal was a 403, an already-subscribed company or a
            // bad request, the user is left on this page with nothing opened,
            // so no branch would change what they do next.
            UIToast.error(t('actions.open_checkout_session.error'));
        }
    });

    return {
        openCheckoutSession: (priceId: string, currency: string) =>
            mutation.mutateAsync({ priceId, currency }),
        loading: mutation.isPending,
        error: mutation.error ? t('actions.open_checkout_session.error') : ''
    };
}
